/* Demo companies for the other GCC countries, derived from the Saudi ones (contracts/seed.json) so every country has
 * one ready-made company per business category without duplicating ~1 MB of seed data.
 * The Go seed builds exactly the same companies (api/internal/seed/gcc.go); demo-logins.json lists their logins and
 * contracts/test-vectors/gcc-demos.json pins a sample of the output for both languages.
 *
 * Rules per country (all deterministic):
 *  - tenant  t-demo-<slug>-<category>, CR "<CC>-<saudi cr>", country VAT format, first/second city of the country
 *  - users   same people, ids "<id>-<slug>", e-mail domain .sa → .<slug> (other domains: first label + "-<slug>")
 *  - staff   Saudis become citizens of the country; pay converted from SAR at the peg and rounded to a "nice" step;
 *            ID numbers, IBANs, banks, mobiles and branches in the country's formats
 *  - weekend 6-day weeks keep Friday off, two-day weekends use the country's weekend
 */
import { COUNTRIES, countryOf, type CountryInfo } from "../../domain/country";
import { round2 } from "../../domain/payroll";
import type * as T from "../types";
import type { DemoCompany, SeedFile } from "./state";

const ROLES = ["owner", "hr", "accountant", "employee"] as const;

/** A SAR amount in the country's currency, rounded to its step (0 stays 0, anything else at least one step). */
export function nice(x: number, c: CountryInfo): number {
  if (!x) return 0;
  const s = c.currency.step;
  return Math.max(s, Math.round((x * c.currency.sarRate) / s) * s);
}

export function demoEmail(email: string, slug: string): string {
  const at = email.lastIndexOf("@");
  if (at < 0) return email;
  const local = email.slice(0, at), domain = email.slice(at + 1);
  if (domain.endsWith(".sa")) return `${local}@${domain.slice(0, -3)}.${slug}`;
  const dot = domain.indexOf(".");
  return dot < 0 ? `${local}@${domain}-${slug}` : `${local}@${domain.slice(0, dot)}-${slug}${domain.slice(dot)}`;
}

const digits = (s: string) => s.replace(/\D/g, "");
const group4 = (s: string) => s.replace(/(.{4})(?=.)/g, "$1 ");

export function demoId(saudiId: string, dob: string, citizen: boolean, c: CountryInfo): string {
  const d = digits(saudiId).padStart(10, "0");
  const y = dob.slice(0, 4), yy = dob.slice(2, 4), mm = dob.slice(5, 7), dd = dob.slice(8, 10);
  const cent = y < "2000" ? "2" : "3";
  switch (c.code) {
    case "AE": return "784" + y + d.slice(3, 10) + "1";
    case "OM": return d.slice(2, 10);
    case "QA": return cent + yy + (citizen ? "634" : "999") + d.slice(5, 10);
    case "BH": return yy + mm + d.slice(5, 10);
    case "KW": return cent + yy + mm + dd + d.slice(5, 10);
    default: return saudiId;
  }
}

export function demoIban(saudiIban: string, bankCode: string, c: CountryInfo): string {
  const d = digits(saudiIban).padEnd(22, "0");
  switch (c.code) {
    case "AE": return group4("AE" + d.slice(0, 21));
    case "OM": return group4("OM" + d.slice(0, 21));
    case "QA": return group4("QA" + d.slice(0, 2) + bankCode + d.slice(2, 22) + "0");
    case "BH": return group4("BH" + d.slice(0, 2) + bankCode + d.slice(2, 16));
    case "KW": return group4("KW" + d.slice(0, 2) + bankCode + d.slice(2, 22) + "00");
    default: return saudiIban;
  }
}

export function demoMobile(saudiMobile: string, c: CountryInfo): string {
  const d = digits(saudiMobile).slice(-9).padStart(9, "0");
  switch (c.code) {
    case "AE": return `+971 ${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 9)}`;
    case "OM": return `+968 9${d.slice(2, 5)} ${d.slice(5, 9)}`;
    case "QA": return `+974 5${d.slice(2, 5)} ${d.slice(5, 9)}`;
    case "BH": return `+973 3${d.slice(2, 5)} ${d.slice(5, 9)}`;
    case "KW": return `+965 5${d.slice(2, 5)} ${d.slice(5, 9)}`;
    default: return saudiMobile;
  }
}

/** Saudi branch / city → the country's first (Jeddah and anything else) or second (Riyadh) city. */
function cityIdx(saudiCity: string): number { return saudiCity === "ruh" || saudiCity === "Riyadh" ? 1 : 0; }
const cityKey = (saudi: string, c: CountryInfo) => c.cities[Math.min(cityIdx(saudi), c.cities.length - 1)][0];
const cityName = (saudi: string, c: CountryInfo) => c.cities[Math.min(cityIdx(saudi), c.cities.length - 1)][1];

function demoVat(vat: string, c: CountryInfo): string {
  const d = digits(vat).padStart(15, "0");
  switch (c.code) {
    case "AE": return "100" + d.slice(3, 15);
    case "BH": return "2" + d.slice(1, 15);
    case "OM": return "OM" + d.slice(5, 15);
    default: return c.vat.pattern ? vat : "";
  }
}

/** The Saudi template per category: Al Waha (the seed company) for its category, the demo company for the others. */
export function saudiTemplates(s: SeedFile): DemoCompany[] {
  const main: DemoCompany = {
    tenant: s.tenant,
    attendance: { shiftStart: s.settings.attendance.shiftStart, shiftEnd: s.settings.attendance.shiftEnd, weekendDays: s.settings.attendance.weekendDays },
    subscription: s.subscription,
    users: ROLES.map((r) => s.users.find((u) => u.tenantId === s.tenant.id && u.role === r)).filter((u): u is T.User => !!u),
    employees: s.employees, sites: s.sites ?? [], leaves: s.leaves, payrollHistory: s.payrollHistory,
  };
  return [main, ...(s.demoCompanies ?? [])].filter((t) => t.tenant.category); // a seed company without a category has no demo twin
}

export function deriveCompany(t: DemoCompany, c: CountryInfo): DemoCompany {
  const slug = c.slug, cat = t.tenant.category;
  const tid = `t-demo-${slug}-${cat}`;
  const idMap = new Map<string, string>();
  const employees = t.employees.map((e): T.Employee => {
    const citizen = e.nationality === "SA";
    const bank = c.banks[e.id % c.banks.length];
    const idNumber = demoId(e.idNumber, e.dob || "1990-01-01", citizen, c);
    idMap.set(e.idNumber, idNumber);
    const basic = nice(e.basic, c);
    return {
      ...e,
      nationality: citizen ? c.code : e.nationality,
      branch: cityKey(e.branch, c),
      basic, housing: nice(e.housing, c), transport: nice(e.transport, c), other: nice(e.other, c),
      idNumber, mobile: demoMobile(e.mobile, c), email: e.email ? demoEmail(e.email, slug) : e.email,
      bank: bank[0], iban: demoIban(e.iban, bank[1], c),
      gosiRegime: "legacy", gosiWage: nice(e.gosiWage, c),
      qiwa: { ...e.qiwa, basic: e.qiwa.basic === e.basic ? basic : nice(e.qiwa.basic, c) },
      ticket: e.ticket ? { ...e.ticket, value: nice(e.ticket.value, c) } : null,
      documents: (e.documents ?? []).map((d) => (d.number === e.idNumber ? { ...d, number: idNumber } : d)),
      dependants: e.dependants ?? [],
    };
  });
  return {
    tenant: {
      id: tid, name: t.tenant.name, nameAr: t.tenant.nameAr, cr: `${c.code}-${t.tenant.cr}`, vat: demoVat(t.tenant.vat, c),
      city: cityName(t.tenant.city, c), category: cat, country: c.code,
    },
    attendance: { ...t.attendance, weekendDays: (t.attendance.weekendDays ?? []).length >= 2 ? [...c.weekend] : [...(t.attendance.weekendDays ?? [])] },
    subscription: t.subscription,
    users: t.users.map((u) => ({ ...u, id: `${u.id}-${slug}`, email: demoEmail(u.email, slug), tenantId: tid })),
    employees,
    sites: t.sites.map((x) => ({ ...x, city: cityName(x.city, c), allowance: nice(x.allowance, c), contractValue: nice(x.contractValue, c), employeeIds: x.employeeIds ?? [] })),
    leaves: t.leaves,
    payrollHistory: t.payrollHistory.map((h) => ({ ...h, gross: round2(h.gross * c.currency.sarRate) })),
  };
}

/** Every derived company (country order, then category order of the Saudi templates). */
export function gccDemoCompanies(s: SeedFile): DemoCompany[] {
  const tpl = saudiTemplates(s);
  return COUNTRIES.filter((c) => c.code !== "SA").flatMap((c) => tpl.map((t) => deriveCompany(t, c)));
}

/** Admin summary rows of the derived companies (from the Saudi template's row). */
export function gccDemoSummaries(s: SeedFile, companies: DemoCompany[]): T.TenantSummary[] {
  const tplRow = new Map<string, T.TenantSummary>();
  for (const t of saudiTemplates(s)) {
    const row = s.tenants.find((x) => x.id === t.tenant.id);
    if (row) tplRow.set(t.tenant.category, row);
  }
  return companies.flatMap((d) => {
    const row = tplRow.get(d.tenant.category);
    return row ? [{ ...row, id: d.tenant.id, name: d.tenant.name, cr: d.tenant.cr, city: d.tenant.city, country: countryOf(d.tenant.country).code, aiCredits: 0 }] : [];
  });
}
